import { ref, onValue } from "firebase/database";
import { db } from "./firebase";

export type Category = "kraftskiva" | "midsommar" | "jul" | "allmant";
export type Source = "user" | "curated" | "snapsdags";

export interface CuratedSong {
  slug: string;
  name: string;
  melody: string;
  text: string;
  category: Category | string;
  source_url?: string;
  copyright_note?: string;
}

export interface Song {
  key: string;          // stable unique key: "u12" | "c-helan-gar"
  id?: number;          // numeric id for user songs
  slug?: string;        // slug for curated songs
  name: string;
  melody: string;
  text?: string;
  source: Source;
  category: Category;
  url: string;          // canonical page for this song
  sourceUrl?: string;
  copyrightNote?: string;
}

export const CATEGORY_LABELS: Record<Category, string> = {
  kraftskiva: "Kräftskiva",
  midsommar: "Midsommar",
  jul: "Jul",
  allmant: "Året runt",
};

export function deriveCategory(name: string, melody: string, text?: string): Category {
  const hay = `${name} ${melody} ${text || ""}`.toLowerCase();
  if (/kräft|kraft|dill|skaldjur/.test(hay)) return "kraftskiva";
  if (/jul|tomte|glögg|glogg|lucia|skinka|nyår/.test(hay)) return "jul";
  if (/midsommar|sill|vårvind|blomstertid|sommar|majstång/.test(hay)) return "midsommar";
  return "allmant";
}

export function melodyLabel(melody?: string): string {
  const m = (melody || "").trim();
  return !m || m === "-" ? "Egen melodi" : m;
}


function normaliseCurated(c: CuratedSong): Song {
  const cat = (["kraftskiva", "midsommar", "jul", "allmant"].includes(String(c.category))
    ? (c.category as Category)
    : deriveCategory(c.name, c.melody, c.text));
  return {
    key: `c-${c.slug}`,
    slug: c.slug,
    name: c.name,
    melody: c.melody || "",
    text: c.text,
    source: "curated",
    category: cat,
    url: `/klassiker/${c.slug}/`,
    sourceUrl: c.source_url,
    copyrightNote: c.copyright_note,
  };
}

/**
 * Subscribes to the unified catalogue.
 * `songs/` = visitor submissions (never modified). Normalised into one shape with
 * explicit source/category attributes.
 */
export function subscribeToCatalogue(
  onSongs: (songs: Song[]) => void,
  onError?: (message: string) => void
): void {
  let userSongs: Song[] = [];
  let curated: Song[] = [];
  const emit = () => onSongs([...userSongs, ...curated]);

  onValue(
    ref(db, "songs"),
    (snap) => {
      const data = snap.val() || {};
      userSongs = Object.entries(data)
        .map(([key, value]) => {
          const v = value as any;
          if (!v || !v.name) return null;
          const id = parseInt(key, 10);
          return {
            key: `u${id}`,
            id,
            name: String(v.name),
            melody: String(v.melody || ""),
            text: v.text ? String(v.text) : undefined,
            source: "user" as Source,
            category: deriveCategory(String(v.name), String(v.melody || ""), v.text),
            url: `/song/${id}`,
          } as Song;
        })
        .filter((s): s is Song => s !== null)
        .sort((a, b) => (a.id || 0) - (b.id || 0));
      emit();
    },
    (err) => {
      console.error("Error fetching songs:", err);
      onError && onError("Kunde inte hämta visorna just nu. Försök igen om en stund.");
    }
  );

}
